import ConfirmModal from '@/components/ConfirmModal';
import {
  abandonExchangeLoadingSelector,
  cancelBookingLoadingSelector,
} from '@/features/loading/loadingSelectors';
import { resetChangeTripsSelection } from '@/features/manage/manageActions';
import { resetPurchase } from '@/features/purchase/purchaseActions';
import { purchaseFlowBookingIdSelector } from '@/features/purchase/purchaseSelectors';
import { useCancelInProgressPurchase } from '@/hooks/useCancelInProgressPurchase';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { useStartedExchangeOperation } from '@/hooks/useStartedExchangeOperation';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import { persistor } from '@/store';
import { useDispatch, useSelector } from '@/store/utils';
import { Typography } from '@packages/webshop-design-system';
import type { MouseEvent, ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const CHECKOUT_PATH = '/purchase/checkout';

type CancelFlow = 'purchase' | 'exchange';

const MODAL_COPY: Record<
  CancelFlow,
  { title: TransTextKeys; description: TransTextKeys }
> = {
  purchase: {
    title: 'cancelPurchase',
    description: 'areYouSureYouWantToCancelThePurchase',
  },
  exchange: { title: 'cancelChange', description: 'cancelChangeConfirmation' },
};

/**
 * Guards the header "Purchase" link: leaving an in-progress purchase must not
 * silently drop redux/localStorage while the booking still lives on the BE.
 * If there's an in-progress booking (or a started change-booking exchange),
 * clicking opens a cancellation confirmation; only confirming runs the real
 * BE cancellation + reset. With nothing in progress the link navigates
 * normally (after a clean reset of any stray local selection).
 */
export function usePurchaseNavGuard(): {
  handlePurchaseClick: (event: MouseEvent<HTMLAnchorElement>) => void;
  cancelModal: ReactElement;
} {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setCurrentSearchFormValues } = useSearchWidget();
  const bookingId = useSelector(purchaseFlowBookingIdSelector);
  const activeExchangeOperation = useStartedExchangeOperation();
  const { cancelPurchaseBooking, abandonExchangeFlow } =
    useCancelInProgressPurchase();
  const isCancellingBooking = useSelector(cancelBookingLoadingSelector);
  const isAbandoningExchange = useSelector(abandonExchangeLoadingSelector);

  const [flow, setFlow] = useState<CancelFlow | null>(null);

  const handlePurchaseClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      // We always drive navigation ourselves so the outcome doesn't depend on
      // the link's (route-dependent) `to`.
      event.preventDefault();

      const isInCheckout = location.pathname === CHECKOUT_PATH;

      // In checkout the user is committed to a booking — starting a new
      // purchase means cancelling it, so confirm first.
      if (isInCheckout && bookingId) {
        setFlow('purchase');
        return;
      }
      if (isInCheckout && activeExchangeOperation) {
        setFlow('exchange');
        return;
      }

      // Outside checkout with something in progress: just go to /purchase and
      // let its layout resume the booking (redirect back to checkout). Don't
      // reset — the booking must survive.
      if (bookingId || activeExchangeOperation) {
        navigate('/purchase');
        return;
      }

      // Nothing in progress — start a clean purchase.
      dispatch(resetPurchase());
      dispatch(resetChangeTripsSelection());
      setCurrentSearchFormValues(undefined);
      void persistor.purge();
      navigate('/purchase');
    },
    [
      activeExchangeOperation,
      bookingId,
      dispatch,
      location.pathname,
      navigate,
      setCurrentSearchFormValues,
    ]
  );

  const copy = flow ? MODAL_COPY[flow] : MODAL_COPY.purchase;

  const cancelModal = (
    <ConfirmModal
      open={flow !== null}
      setOpen={(open) => {
        if (!open) setFlow(null);
      }}
      title={<TransText i18nKey={copy.title} />}
      description={
        <Typography variant="paragraph">
          <TransText i18nKey={copy.description} />
        </Typography>
      }
      cancelLabel="dismiss"
      confirmLabel="confirm"
      onConfirm={
        flow === 'exchange' ? abandonExchangeFlow : cancelPurchaseBooking
      }
      isConfirmLoading={
        flow === 'exchange' ? isAbandoningExchange : isCancellingBooking
      }
    />
  );

  return { handlePurchaseClick, cancelModal };
}
